import { MediaErrorDetail } from '@vidstack/react';
import { MeetingRecording } from 'api/meeting/types';
import { useMeetingRecordingQuery } from 'queries/meeting';
import { useCallback, useEffect, useRef } from 'react';

// MediaError codes: 2 = Network Error, 4 = Invalid Resource Error
const RETRYABLE_ERROR_CODES = [2, 4] as const;
const MAX_RETRY_COUNT = 3;

type UseRecordingWithRetryProps = {
  meetingId: number;
};
type RefetchMeetingRecordingWithRetryResult = {
  meetingRecording?: MeetingRecording;
  isMeetingRecordingLoading: boolean;
  refetchMeetingRecordingWithRetry: (error: MediaErrorDetail) => Promise<{
    canResumePlayback: boolean;
  }>;
};
export const useGetMeetingRecordingWithRetry = ({
  meetingId,
}: UseRecordingWithRetryProps): RefetchMeetingRecordingWithRetryResult => {
  const {
    data: meetingRecording,
    isLoading: isMeetingRecordingLoading,
    refetch: refetchMeetingRecording,
  } = useMeetingRecordingQuery(meetingId);
  const refetchCountRef = useRef(0);

  useEffect(() => {
    refetchCountRef.current = 0;
  }, [meetingId]);
  const refetchMeetingRecordingWithRetry = useCallback(
    async (error: MediaErrorDetail) => {
      const isRetryableError =
        error.code != null &&
        RETRYABLE_ERROR_CODES.includes(error.code as 2 | 4);

      if (isRetryableError && refetchCountRef.current < MAX_RETRY_COUNT) {
        await refetchMeetingRecording();
        refetchCountRef.current += 1;

        return { canResumePlayback: true };
      }

      return { canResumePlayback: false };
    },
    [refetchMeetingRecording],
  );

  return {
    meetingRecording,
    isMeetingRecordingLoading,
    refetchMeetingRecordingWithRetry,
  };
};
